/**
 * Inadimplentes Page Component
 * 
 * Exibe lista de inadimplentes (defaulters) do Servopa.
 * - Super Admin: vê todos os inadimplentes
 * - Usuário normal: vê apenas seus próprios inadimplentes (baseado no CPF do vendedor)
 */

import { CabecalhoLista, CabecalhoPagina, CartaoStat, cabecalhoTabelaNorth, tabelaBentoHead, tabelaBentoLinha } from '../bento/ui';
import { TendenciaInadimplencia } from './TendenciaInadimplencia';
import { formatBRL as formatCurrency } from '@/lib/format';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { PageSpinner } from '@/components/ui/page-spinner';
import { 
  AlertTriangle,
  DollarSign,
  User,
  Users,
  Filter,
  Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useNavigate } from 'react-router-dom';
import { useRoleDataScope } from '../commons/useRoleDataScope';
import { DetalheInadimplente } from './DetalheInadimplente';
import { calcularParcelasEmAberto } from './parcelasEmAberto';
import { textoOrigemDosDados } from './dataDoRelatorio';
import { useSomenteLeitura } from '../commons/somenteLeitura';
import { BotaoOlhinho, ValoresOcultosProvider } from '../valores-ocultos/ValoresOcultos';
import { useMascara } from '../valores-ocultos/contexto';
import { CotaLink } from '../comissoes/CotaLink';

interface Inadimplente {
  id: number;
  sync_date: string;
  /**
   * Quando a SERVOPA gerou o PDF — não é quando nós baixamos (`sync_date`).
   * O relatório fica dias sem ser republicado: em setembro de 2026 foram quatro
   * dias com o mesmo arquivo, cada dia carimbado como se fosse leitura nova.
   * Nulo nas linhas anteriores à migration 173.
   */
  relatorio_em: string | null;
  vendedor_cpf_cnpj: string;
  vendedor_nome: string;
  cota: string;
  contrato: string;
  consorciado: string;
  celular: string;
  /**
   * Colunas PGO e ATR do relatório do Servopa: parcelas PAGAS e parcelas em
   * ATRASO. A segunda se chamou `dias_atraso` até a migration 165 — nome
   * errado herdado do parser, porque não são dias. A conferência que sustenta
   * isso está em `parcelasEmAberto.ts`. O campo antigo continua declarado
   * enquanto a migration 167 não o remove do banco.
   */
  pgo_atr: number;
  parcelas_em_aberto: number | null;
  dias_atraso?: number | null;
  percentual_mensal: number;
  percentual_difer: number;
  valor_atraso: number;
  created_at: string;
}

interface InadimplentesStats {
  total: number;
  total_valor: number;
  por_vendedor: {
    vendedor_nome: string;
    count: number;
    total_valor: number;
  }[];
}

// O olhinho envolve a tela inteira, inclusive a ficha que abre por cima e o
// spinner da carga (a preferência chega em paralelo e começa escondida).
export const InadimplentesPage = () => (
  <ValoresOcultosProvider>
    <InadimplentesConteudo />
  </ValoresOcultosProvider>
);

const InadimplentesConteudo = () => {
  const { somenteLeitura } = useSomenteLeitura();
  const m = useMascara();
  const navigate = useNavigate();
  const {
    isLoading: scopeLoading,
    isSuperAdmin,
    isManager,
    scopedCpfs,
  } = useRoleDataScope();
  const [inadimplentes, setInadimplentes] = useState<Inadimplente[]>([]);
  const [stats, setStats] = useState<InadimplentesStats | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * `?q=` semeia a busca.
   *
   * É o que permite uma linha do dashboard abrir esta página já filtrada na
   * pessoa clicada, em vez de despejar a lista inteira e deixar a busca por
   * conta de quem clicou. Só o valor INICIAL: depois disso quem manda é a
   * caixa de busca, e digitar não fica brigando com a URL.
   */
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') ?? '');
  const [vendedorFilter, setVendedorFilter] = useState<string>('all');
  const [syncDate, setSyncDate] = useState<string>('');
  // A data do relatório, que é a que vale pra quem lê a tela.
  const [relatorioEm, setRelatorioEm] = useState<string | null>(null);
  /**
   * Linha aberta na ficha de cobrança.
   *
   * Clicar no nome não pula mais direto para o contato: a pergunta de quem
   * está cobrando ("quantas parcelas estão em aberto?") se responde aqui, e a
   * ficha do contato — que fala de venda — continua a um botão de distância.
   */
  const [selecionado, setSelecionado] = useState<Inadimplente | null>(null);

  useEffect(() => {
    if (!scopeLoading) {
      loadData();
    }
  }, [scopeLoading, isSuperAdmin, scopedCpfs]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Buscar inadimplentes (usar a view current para pegar apenas o último sync)
      const { data: inadimplentesData, error } = await supabase
        .from('servopa_inadimplentes_current')
        .select('*')
        .order('valor_atraso', { ascending: false });

      if (error) {
        console.error('Erro ao buscar inadimplentes:', error);
        return;
      }

      // O vendedor que vale é o da VENDA do contrato (`responsavel_*`, migration
      // 228), não o do PDF: a Servopa lista muitas cotas no CNPJ da corretora,
      // e aí a linha não caía na carteira de ninguém. O resto da tela lê
      // vendedor_* — trocar aqui mantém filtro, agrupamento e busca coerentes.
      // Sem as colunas (banco ainda sem a 228), fica o que o PDF diz.
      let scopedData: any[] = (inadimplentesData || []).map((item: any) => ({
        ...item,
        vendedor_cpf_cnpj: item.responsavel_cpf_cnpj ?? item.vendedor_cpf_cnpj,
        vendedor_nome: item.responsavel_nome ?? item.vendedor_nome,
      }));

      if (!isSuperAdmin) {
        if (scopedCpfs.length === 0) {
          scopedData = [];
        } else {
          const cpfSet = new Set(scopedCpfs);
          scopedData = scopedData.filter((item) => cpfSet.has((item.vendedor_cpf_cnpj || '').replace(/\D/g, '')));
        }
      }

      setInadimplentes(scopedData);

      // Calcular estatísticas
      if (scopedData && scopedData.length > 0) {
        setSyncDate(scopedData[0].sync_date);
        setRelatorioEm(scopedData[0].relatorio_em ?? null);
        
        const total = scopedData.length;
        const total_valor = scopedData.reduce((sum, i) => sum + (i.valor_atraso || 0), 0);

        // Agrupar por vendedor
        const porVendedor = scopedData.reduce((acc: any, i) => {
          const key = i.vendedor_nome;
          if (!acc[key]) {
            acc[key] = {
              vendedor_nome: i.vendedor_nome,
              count: 0,
              total_valor: 0
            };
          }
          acc[key].count++;
          acc[key].total_valor += i.valor_atraso || 0;
          return acc;
        }, {});

        setStats({
          total,
          total_valor,
          por_vendedor: Object.values(porVendedor).sort((a: any, b: any) => b.total_valor - a.total_valor) as InadimplentesStats['por_vendedor']
        });
      }

    } catch (error) {
      console.error('Erro ao carregar inadimplentes:', error);
    } finally {
      setLoading(false);
    }
  };

  // Lista única de vendedores pro filtro dropdown
  const vendedorOptions = Array.from(
    new Set(inadimplentes.map((i) => i.vendedor_nome).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

  // Filtrar inadimplentes pelo termo de busca + vendedor selecionado
  const filteredInadimplentes = inadimplentes.filter(i => {
    if (vendedorFilter !== 'all' && i.vendedor_nome !== vendedorFilter) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      i.consorciado.toLowerCase().includes(term) ||
      i.contrato.toLowerCase().includes(term) ||
      i.cota.toLowerCase().includes(term) ||
      i.vendedor_nome.toLowerCase().includes(term)
    );
  });

  const handleOpenContact = async (consorciado: string) => {
    if (!consorciado) return;
    try {
      const { data, error } = await supabase
        .from('contacts')
        .select('id')
        .eq('details->>name', consorciado)
        .limit(1);

      if (error) {
        console.error('Erro ao buscar contato:', error);
        alert('Não foi possível abrir o contato.');
        return;
      }

      const contactId = data?.[0]?.id;
      if (!contactId) {
        alert('Contato não encontrado para este consorciado.');
        return;
      }

      navigate(`/contacts/${contactId}/show`);
    } catch (err) {
      console.error('Erro ao abrir contato:', err);
      alert('Não foi possível abrir o contato.');
    }
  };

  if (loading || scopeLoading) {
    return <PageSpinner />;
  }

  return (
    <div className="space-y-4 p-3 sm:p-6">
      {/* Cabeçalho */}
      <div>
        <CabecalhoPagina
          titulo="Inadimplentes"
          aoLadoDoTitulo={<BotaoOlhinho />}
          descricao={textoOrigemDosDados({ relatorioEm, syncDate })}
        />
        {/* O escopo, como antes: quem é super admin vê tudo, gerente vê os
            convidados, o vendedor só os próprios. */}
        <span className="mt-3 inline-flex items-center gap-1.5 rounded-[4px] bg-[#EEF1F6] px-2 py-0.5 text-[11.5px] font-semibold text-primary before:h-1.5 before:w-1.5 before:rounded-full before:bg-current">
          {isSuperAdmin
            ? 'Visualização completa (super admin)'
            : isManager
              ? 'Inadimplentes dos usuários convidados'
              : 'Somente inadimplentes próprios'}
        </span>
      </div>

      {/* Cards de resumo */}
      {stats && (
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 lg:grid-cols-4">
          <CartaoStat
            rotulo="Total de inadimplentes"
            valor={m(stats.total)}
            detalhe="contratos em atraso"
            icone={<Users className="h-4 w-4" />}
          />
          <CartaoStat
            rotulo="Valor total em atraso"
            valor={m(formatCurrency(stats.total_valor))}
            detalhe="soma total"
            tom="danger"
            icone={<DollarSign className="h-4 w-4" />}
          />
          <CartaoStat
            rotulo="Média por contrato"
            valor={m(formatCurrency(stats.total > 0 ? stats.total_valor / stats.total : 0))}
            detalhe="valor médio"
            icone={<DollarSign className="h-4 w-4" />}
          />
          <TendenciaInadimplencia />
        </div>
      )}

      {/* Search and Filters */}
      <div className="cartao bg-card px-4 py-4 sm:px-5">
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-[13.5px] font-semibold tracking-[-0.005em] text-marinho">Lista de inadimplentes</h3>
            <div className="flex gap-2">
              {!somenteLeitura && (
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Exportar
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-4">
            <div className="flex items-center gap-2 flex-1">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, contrato, cota, vendedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-sm"
              />
            </div>
            <Select value={vendedorFilter} onValueChange={setVendedorFilter}>
              <SelectTrigger className="sm:w-64">
                <SelectValue placeholder="Filtrar por vendedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os vendedores</SelectItem>
                {vendedorOptions.map((v) => (
                  <SelectItem key={v} value={v}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          {filteredInadimplentes.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Nenhum inadimplente encontrado.</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm ? 'Nenhum resultado para a busca.' : 'Nenhum contrato em atraso.'}
              </p>
            </div>
          ) : (
            <div>
              <Table>
                <TableHeader>
                  <TableRow className={`${tabelaBentoLinha} ${cabecalhoTabelaNorth}`}>
                    <TableHead className={tabelaBentoHead}>Contrato/Cota</TableHead>
                    <TableHead className={tabelaBentoHead}>Consorciado</TableHead>
                    <TableHead className={tabelaBentoHead}>Vendedor</TableHead>
                    <TableHead className={`${tabelaBentoHead} text-center`}>Parcelas pagas</TableHead>
                    <TableHead className={`${tabelaBentoHead} text-center`}>Parcelas em aberto</TableHead>
                    <TableHead className={`${tabelaBentoHead} text-right`}>Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInadimplentes.map((inadimplente) => (
                    <TableRow key={inadimplente.id} className={`${tabelaBentoLinha} hover:bg-[#FAFBFC]`}>
                      <TableCell className="font-medium">
                        <div className="flex flex-col">
                          <span className="font-mono text-sm">{inadimplente.contrato}</span>
                          <span className="text-xs text-muted-foreground">{inadimplente.cota ? <CotaLink cota={inadimplente.cota} contrato={inadimplente.contrato} /> : null}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <button
                            type="button"
                            onClick={() => setSelecionado(inadimplente)}
                            className="text-left text-sm font-medium text-primary hover:underline"
                          >
                            {inadimplente.consorciado}
                          </button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="text-sm">{inadimplente.vendedor_nome}</span>
                          <span className="text-xs text-muted-foreground">{inadimplente.vendedor_cpf_cnpj}</span>
                        </div>
                      </TableCell>
                      {/* Os rótulos estavam trocados: quem apanhava do "destructive"
                          era o consorciado que MAIS pagou. O alarme é o que está em
                          aberto — uma parcela é atraso de rotina, duas ou mais já é
                          risco de exclusão do grupo. */}
                      <TableCell className="text-center tabular-nums">
                        {m(calcularParcelasEmAberto(inadimplente).pagas ?? '—')}
                      </TableCell>
                      <TableCell className="text-center">
                        {(() => {
                          const emAberto = calcularParcelasEmAberto(inadimplente).emAberto;
                          if (emAberto === null) {
                            return <span className="text-xs text-muted-foreground">sem dado</span>;
                          }
                          return (
                            <span
                              className={`inline-flex items-center gap-1.5 rounded-[4px] px-2 py-0.5 text-[11.5px] font-semibold tabular-nums before:h-1.5 before:w-1.5 before:rounded-full before:bg-current ${
                                emAberto > 1 ? 'bg-[#FDE8E6] text-[#B42318]' : 'bg-[#FCEFC9] text-[#92560A]'
                              }`}
                            >
                              {m(emAberto)}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-right font-bold">
                        <span className={`tabular-nums ${inadimplente.valor_atraso > 5000 ? "text-[#b91c1c]" : ""}`}>
                          {m(formatCurrency(inadimplente.valor_atraso))}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <DetalheInadimplente
        inadimplente={selecionado}
        onFechar={() => setSelecionado(null)}
        onAbrirContato={handleOpenContact}
      />

      {/* Vendedores Stats (apenas para super admin) */}
      {isSuperAdmin && stats && stats.por_vendedor.length > 0 && (
        <div className="cartao bg-card px-4 py-4 sm:px-5">
          <CabecalhoLista titulo="Inadimplentes por vendedor" extra={`${m(stats.por_vendedor.length)} vendedores`} />
          <div className="mt-2">
            <div>
              {stats.por_vendedor.map((vendedor, idx) => (
                <div key={idx} className="flex items-center justify-between border-b border-[rgba(53,70,96,0.09)] py-3 last:border-b-0">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">{vendedor.vendedor_nome}</p>
                      <p className="text-sm text-muted-foreground">{m(vendedor.count)} contratos</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold tabular-nums text-[#b91c1c]">{m(formatCurrency(vendedor.total_valor))}</p>
                    <p className="text-sm text-muted-foreground">
                      Média: {m(formatCurrency(vendedor.total_valor / vendedor.count))}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
